import React, { useEffect, useState } from 'react';
import Card from 'components/common/Card';
import TextArea from 'components/TextArea';
import TextInput from 'components/TextInput';
import config from '../../../config';
import numeral from 'numeral';
import useAddCardDialog from '../useAddCardDialog';
import { FORM1 } from 'constants/orders';

import {
  LineItem,
  StepTitle,
  CardBrand,
  CardNumber,
  CardLabel,
  CardExpiry,
  SummaryText,
  SummaryItem,
  CardContain,
  StepSubTitle,
  LineItemText,
  ContentContain,
  Required,
  LoadingContain,
  StepInformation,
  CardNumberContain
} from '../CreateStyled';

import Notice from 'components/Notice';
import LoadingSpinner from 'components/LoadingSpinner';

export const StepCheckout = ({
  addCard,
  is_admin,
  order,
  loading,
  complete,
  fetchCard,
  is_postpaid,
  activeCard,
  orderTitle,
  onComplete,
  planParcel,
  serviceFee,
  loadingCard,
  updateOrder,
  renderActions,
  updateSession,
  getCheckout,
  addressDisplay,
  selectedCouncil,
  titleServiceFee,
  strataServiceFee,
  selectedStrataCert,
  selectedCertificate,
  loadingCheckout,
  loadingSessionUpdate,
  orderType,
  strataFinancialsServiceFee
}) => {
  const [ state, setState ] = useState(order.additional_information || '');
  const [ settlement, setSettlement ] = useState(order.proposed_settlement_date || '');
  const [ errors, setErrors ] = useState(null);

  const [ renderDialog, handleOpen, handleClose ] = useAddCardDialog();
  const preAuthRequired = order.checkout && order.checkout.preauth;

  const isSettlementVisible = !!selectedStrataCert && orderType !== FORM1;
  const isSettlementRequired = isSettlementVisible && order.address_state !== 'SA';

  useEffect(
    function fetchCheckout () {
      getCheckout(orderType)
    }, 
    [ getCheckout, orderType ]
  );

  useEffect(
    function onOrderCompleted () {
      if (complete) onComplete();
    }, 
    [ onComplete, complete ]
  );

  useEffect(
    function fetchPaymentCard () {
      fetchCard();
    }, 
    [ fetchCard ]
  );

  useEffect(
    function onPaymentOptionAdded () {
      if (activeCard) handleClose();
    }, 
    [activeCard]
  )

  function updateInfo () {
    setErrors(null);

    var values = { 
      additional_information: state
    };

    if (isSettlementVisible) {   
      var trimmedSettlement = (settlement || '').trim();

      if (!trimmedSettlement && isSettlementRequired) {
        setErrors({ settlement: 'You must provide the proposed settlement date.' });
        
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });

        return false;
      }

      values.proposed_settlement_date = trimmedSettlement;
    }

    return updateOrder(values);
  };

  function handleChange (name, value) {
    setState(value);
  }

  if (loadingCheckout || loadingCard || loadingSessionUpdate) {
    return (
      <Card.Content>
        <ContentContain>
          <LoadingContain>
            <LoadingSpinner large />
            Loading...
          </LoadingContain>
        </ContentContain>
      </Card.Content>
    );
  }

  var nextLabel = 'Pay & Submit';

  if (is_postpaid) {
    nextLabel = 'Submit';
  }

  if (!activeCard && !is_postpaid) {
    nextLabel = "Add Card";
  } else if (preAuthRequired) {
    nextLabel = 'Authorise & Submit'
  }

  return (
    <Card.Content>
      <ContentContain>
        <StepTitle>Summary</StepTitle>

        {
          !is_postpaid && !preAuthRequired &&
          <StepInformation>
            Review your order below before clicking submit. Your credit card will be charged the full amount on submitting the order.
          </StepInformation>
        }

        {
          !is_postpaid && preAuthRequired &&
          <div style={{ paddingBottom: '1rem' }}>

            <Notice
              secondaryColor="#fffbeb" 
              color="#fbbf24" 
              text={
                <span style={{ display:'block', padding: '0.5rem', lineHeight: 1.5, fontSize: '0.875rem', color: '#d97706' }}><span style={{ color: '#b45309', fontWeight: 500 }}>*Pre-Auth Required </span>
                  Please note: Your card will be pre-authorised for the total amount at checkout. The final charge may vary slightly based on GST adjustments made to your order before it's processed. Only the final total will be charged to your card upon order completion.
                </span>
              } 
            />
          </div>
        }

        {
          !!is_postpaid &&
          <div style={{ paddingBottom: '1rem' }}>
            <StepInformation>
              Review your order below before clicking submit.
            </StepInformation>

            <Notice
              secondaryColor="#fffbeb" 
              color="#fbbf24" 
              text={
                <span style={{ display:'block', padding: '0.5rem', lineHeight: 1.5, fontSize: '0.875rem', color: '#d97706' }}><span style={{ color: '#b45309', fontWeight: 500 }}>*Please note: </span>
                  Search charges, including service fees, will be added to your monthly account. You will receive a statement at the end of each month. {preAuthRequired ? 'The final amount may vary slightly due to GST adjustments before processing. Only the final total will be charged to your account upon order completion.' : ''}
                </span>
              } 
            />
          </div>
        }
        {
          isSettlementVisible &&
          <StepInformation>
            Proposed Settlement Date {isSettlementRequired && <Required>*</Required>}
            <TextInput
              value={settlement}
              errorText={(errors && errors.settlement) || ''}
              name="proposed_settlement_date "
              placeholder="Proposed Settlement Date"
              onChange={(name, value) => setSettlement(value || '')}
            />
          </StepInformation>
        }

        <StepInformation>
          Additional instructions for the order:
          <TextArea
            value={state}
            onChange={handleChange}
            name="additional_information"
            placeholder="Additional Information"
          />
        </StepInformation>

        {!is_postpaid && !!activeCard && (
          <CardContain>
            <CardBrand
              src={`${
                config.site.baseName ? `/${config.site.baseName}` : ''
              }/img/cards/${activeCard.brand}.svg`}
            />
            <CardLabel>Card Number</CardLabel>
            <CardNumber>
              <CardNumberContain placeholder>****</CardNumberContain>
              <CardNumberContain placeholder>****</CardNumberContain>
              <CardNumberContain placeholder>****</CardNumberContain>
              <CardNumberContain>{activeCard.last4}</CardNumberContain>
            </CardNumber>
            <CardLabel>Card Expiry</CardLabel>
            <CardExpiry>
              {activeCard.exp_month} / {activeCard.exp_year}
            </CardExpiry>
          </CardContain>
        )}

        <LineItem>
          <StepSubTitle>Address</StepSubTitle>
          <LineItemText>{addressDisplay}{planParcel && <span>{' — '}{planParcel}</span>}</LineItemText>
        </LineItem>

        {!!selectedCouncil &&
          <LineItem>
            <StepSubTitle>Council</StepSubTitle>
            <LineItemText>{selectedCouncil}</LineItemText>
          </LineItem>
        }
        {!!order.checkout &&

        <LineItem>
          <StepSubTitle>Order Items</StepSubTitle>

          {renderOrderItems(mapCheckout(order.checkout))}

          <SummaryItem>
            <LineItemText fontSize={1.5} removeMargin>
              {preAuthRequired ? "Auth Amount" : "Total"}
            </LineItemText>
            <SummaryText fontSize={1.5}>
              {numeral(order.checkout.total).format('$0,0.00')}
            </SummaryText>
          </SummaryItem>
        </LineItem>}

        {loading && (
          <LoadingContain>
            <LoadingSpinner large />
            Processing Order...
          </LoadingContain>
        )}

        {renderDialog({
          getCard: fetchCard,
          loadingCard: loadingCard,
          addCard: addCard,
          is_admin: is_admin
        })}

        {!loading && renderActions({ 
          submit: true, 
          nextLabel: nextLabel, 
          onBack: updateInfo, 
          onSubmit: updateInfo, 
          cardRequired: !activeCard && !is_postpaid, 
          handleCard: handleOpen 
        })}

      </ContentContain>
    </Card.Content>
  );
};

function mapCheckout (checkout) {
  const ITEM_BLACKLIST = ['Form 1 Document'];

  return checkout.items.filter(i => !~ITEM_BLACKLIST.indexOf(i.name))
}

function renderOrderItems (items) {
  return items.map(item => (
    <SummaryItem key={item.name}>
      <LineItemText removeMargin>{item.name}</LineItemText>
      <SummaryText>{numeral(item.price).format('$0,0.00')}</SummaryText>
    </SummaryItem>
  ));
}

StepCheckout.defaultProps = {
  inputErrors: {},
  onValueChange: () => null
};

export default StepCheckout;
